import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import {
  loadUserData,
  saveUserData,
  type UserData,
  type UserProfile,
} from "@/lib/store";
import type { Activity, Certificate, TimelineEvent, UploadedDocument } from "@/lib/mock";

type DataContextValue = {
  data: UserData;
  updateProfile: (patch: Partial<UserProfile>) => void;
  addCertificate: (cert: Certificate, timeline?: TimelineEvent[]) => void;
  addDocument: (doc: UploadedDocument) => void;
  removeDocument: (id: string) => void;
  pushActivity: (activity: Activity) => void;
};

const DataContext = createContext<DataContextValue | null>(null);

const EMPTY_DATA: UserData = {
  profile: {
    firstName: "",
    secondName: "",
    firstSurname: "",
    secondSurname: "",
    email: "",
    documentType: "CC",
    documentNumber: "",
    city: "",
    department: "",
    address: "",
    kind: "natural",
    companyName: null,
  },
  certificates: [],
  documents: [],
  activity: [],
  timelines: {},
};

export function DataProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [data, setData] = useState<UserData>(EMPTY_DATA);

  // Reload (or seed) per-user store whenever the authenticated subject changes.
  useEffect(() => {
    if (auth.status !== "authenticated") {
      setData(EMPTY_DATA);
      return;
    }
    setData(loadUserData(auth.user.id, auth.user.role, auth.user.phone));
  }, [auth.status, auth.status === "authenticated" ? auth.user.id : null]);

  // Persist any change for the current user.
  useEffect(() => {
    if (auth.status !== "authenticated") return;
    saveUserData(auth.user.id, data);
  }, [auth, data]);

  const updateProfile = useCallback((patch: Partial<UserProfile>) => {
    setData((d) => ({ ...d, profile: { ...d.profile, ...patch } }));
  }, []);

  const addCertificate = useCallback(
    (cert: Certificate, timeline?: TimelineEvent[]) => {
      setData((d) => ({
        ...d,
        certificates: [cert, ...d.certificates],
        timelines: timeline ? { ...d.timelines, [cert.id]: timeline } : d.timelines,
      }));
    },
    []
  );

  const addDocument = useCallback((doc: UploadedDocument) => {
    setData((d) => ({ ...d, documents: [doc, ...d.documents] }));
  }, []);

  const removeDocument = useCallback((id: string) => {
    setData((d) => ({ ...d, documents: d.documents.filter((x) => x.id !== id) }));
  }, []);

  const pushActivity = useCallback((activity: Activity) => {
    setData((d) => ({ ...d, activity: [activity, ...d.activity].slice(0, 25) }));
  }, []);

  const value = useMemo<DataContextValue>(
    () => ({ data, updateProfile, addCertificate, addDocument, removeDocument, pushActivity }),
    [data, updateProfile, addCertificate, addDocument, removeDocument, pushActivity]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used inside DataProvider");
  return ctx;
}
